function readLogTimestamp(log) {
  if (!log) return null
  const orderData = log.orderData || {}
  let ts = orderData.timestamp || log.processedAt || log.timestamp
  if (!ts) return null
  if (typeof ts === 'number' && ts < 10000000000) ts *= 1000
  const ms = new Date(ts).getTime()
  return Number.isFinite(ms) ? ms : null
}

function sumLots(rows) {
  return (rows || []).reduce((acc, row) => {
    const qty = Number(row?.q)
    const price = Number(row?.p)
    if (!(qty > 0) || !(price > 0)) return acc
    acc.qty += qty
    acc.notional += price * qty
    return acc
  }, { qty: 0, notional: 0 })
}

const STRUCTURE_CLOSE_PURPOSES = new Set(['closeLong', 'manualSell', 'sellSpot', 'closeShort', 'hedgeBuy', 'closeHedge', 'reconcileSpotShort'])

function closedPnlOf(log) {
  if (!STRUCTURE_CLOSE_PURPOSES.has(log?.purpose)) return 0
  const pnl = Number(log?.metrics?.pnl)
  return Number.isFinite(pnl) ? pnl : 0
}

export function pmpLevelsFromSnapshot(snapshot, { spot = true, futures = true } = {}) {
  const longs = []
  const shorts = []
  if (spot) {
    longs.push(...(snapshot?.spotLongs || []))
    shorts.push(...(snapshot?.spotShorts || []))
  }
  if (futures) {
    longs.push(...(snapshot?.futuresLongs || []))
    shorts.push(...(snapshot?.futuresShorts || []))
  }
  const longBook = sumLots(longs)
  const shortBook = sumLots(shorts)
  return {
    pmpLong: longBook.qty > 0 ? longBook.notional / longBook.qty : null,
    pmpShort: shortBook.qty > 0 ? shortBook.notional / shortBook.qty : null,
    longQty: longBook.qty,
    shortQty: shortBook.qty,
  }
}

export function sidePnlFromSnapshot(snapshot, markPrice, { spot = true, futures = true } = {}) {
  const levels = pmpLevelsFromSnapshot(snapshot, { spot, futures })
  const mark = Number(markPrice)
  if (!(mark > 0)) {
    return { ...levels, longPnl: null, shortPnl: null, netPnl: null }
  }
  const longPnl = levels.longQty > 0 ? levels.longQty * (mark - levels.pmpLong) : 0
  const shortPnl = levels.shortQty > 0 ? levels.shortQty * (levels.pmpShort - mark) : 0
  return { ...levels, longPnl, shortPnl, netPnl: longPnl + shortPnl }
}

export function edgesPointFromMetrics(metrics) {
  const snapshot = metrics?.manualsSnapshot
  const mark = Number(metrics?.currentPrice)
  if (!snapshot || !(mark > 0)) return null

  const longs = sumLots([...(snapshot.spotLongs || []), ...(snapshot.futuresLongs || [])])
  const shorts = sumLots([...(snapshot.spotShorts || []), ...(snapshot.futuresShorts || [])])
  const pmpLong = longs.qty > 0 ? longs.notional / longs.qty : 0
  const pmpShort = shorts.qty > 0 ? shorts.notional / shorts.qty : 0
  const pairedQty = Math.min(longs.qty, shorts.qty)

  return {
    openPnl: longs.qty * (mark - pmpLong) + shorts.qty * (pmpShort - mark),
    lockedBasisPnl: pairedQty * (pmpShort - pmpLong),
  }
}

export function buildEdgesSeries(logs) {
  const points = (logs || [])
    .map((log, index) => {
      if (!log || log.processed === false) return null
      const point = edgesPointFromMetrics(log.metrics)
      const ts = readLogTimestamp(log)
      if (!point || ts == null) return null
      return { ts, index, eventClosedPnl: closedPnlOf(log), ...point }
    })
    .filter(Boolean)
    .sort((a, b) => a.ts - b.ts || a.index - b.index)

  let closedPnl = 0
  const withStructural = points.map((point) => {
    closedPnl += point.eventClosedPnl
    return {
      ...point,
      closedPnl,
      structuralPnl: closedPnl + point.lockedBasisPnl,
    }
  })

  const initialStructural = withStructural[0]?.structuralPnl ?? 0
  return withStructural.map((point) => ({
    ...point,
    structuralDelta: point.structuralPnl - initialStructural,
  }))
}

// Keeps the cumulative close from the full series, and plots the last snapshot
// at or before each aggregated timestamp. The first snapshot stays, so Líquido
// still starts at the beginning of the period.
export function sampleEdgesSeries(series, timestamps) {
  const full = Array.isArray(series) ? series : []
  if (!full.length) return []

  const marks = (timestamps || [])
    .filter((ts) => Number.isFinite(ts))
    .sort((a, b) => a - b)
  if (!marks.length) {
    return full.map((point) => ({ ...point, plotTs: point.ts }))
  }

  const sampled = []
  let cursor = 0
  for (const mark of marks) {
    while (cursor + 1 < full.length && full[cursor + 1].ts <= mark) cursor++
    if (full[cursor].ts > mark) continue
    const prev = sampled[sampled.length - 1]
    if (prev && prev.plotTs === mark) continue
    sampled.push({ ...full[cursor], plotTs: mark })
  }

  if (!sampled.length) {
    return full.map((point) => ({ ...point, plotTs: point.ts }))
  }

  if (sampled[0].ts !== full[0].ts) {
    sampled.unshift({ ...full[0], plotTs: full[0].ts })
  }

  return sampled
}
